import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DA_Tooltip } from "@danishagro/shared/src/components/atoms/Tooltip/Tooltip.component";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { formatNumber } from "@danishagro/shared/src/helpers/formatNumber.helper";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import { useDynamicOverlay } from "@danishagro/shared/src/hooks/dynamicOverlay/useDynamicOverlay.hook";
import { DiscountTable } from "@organisms/DiscountTable"; // Adjust the import path as needed
import { DA_EmptyStateNoPrice } from "@molecules/EmptyState/NoPrice/NoPrice.component";
import S from "./ProductListPrice.module.scss";

interface QuantityDiscounts {
    isCollection: boolean;
    type: string;
    value?: string;
}

interface QuantityDiscountValue {
    FromQuantity: number;
    ToQuantity: number;
    Price: number;
}

export interface DA_ProductListPriceProps {
    listPrice?: number;
    salesPrice?: number;
    hasVariants?: boolean;
    quantityDiscounts?: QuantityDiscounts;
    onClick?: () => void;
    quantity: number;
}

export const DA_ProductListPrice = ({
    listPrice,
    salesPrice,
    hasVariants,
    quantityDiscounts,
    quantity,
}: DA_ProductListPriceProps) => {
    const { currentCulture } = useAppData();
    const { getDictionaryString } = useTranslations();
    const { isMobile } = useScreen();
    const { showOverlay } = useDynamicOverlay();

    const [discounts, setDiscounts] = useState<
        {
            FromQuantity: number;
            ToQuantity: number;
            Price: number;
        }[]
    >([]);

    const hasQuantityDiscounts = discounts.some((discount) => discount.FromQuantity > 0) ?? false;

    const hasDiscount = salesPrice && salesPrice < listPrice;

    useEffect(() => {
        // Parse the JSON string of quantity discounts, if available
        if (!quantityDiscounts?.value) return;
        try {
            const parsedValue = JSON.parse(quantityDiscounts.value) as QuantityDiscountValue[];
            setDiscounts(parsedValue);
        } catch (error) {
            console.error("Failed to parse quantityDiscounts.value:", error);
        }
    }, [quantityDiscounts?.value]);

    const FromComponent = () => {
        return hasVariants ? (
            <span>{getDictionaryString("from", { uppercaseFirst: true })} </span>
        ) : null;
    };

    const discountTable = useMemo(() => {
        if (hasQuantityDiscounts && discounts) {
            return discounts.map((discount) => ({
                quantityFrom: discount.FromQuantity,
                quantityTo: discount.ToQuantity || Infinity,
                price: discount.Price,
            }));
        }
        return [];
    }, [discounts, hasQuantityDiscounts]);

    const discountAmountTitle = getDictionaryString("discount.discountAmountTitle");

    const openDynamicDiscountOverlay = useCallback(() => {
        showOverlay({
            id: "discount_amount_open",
            title: discountAmountTitle,
            content: <DiscountTable version="v1" type="Mobile" discountTable={discountTable} />,
        });
    }, [showOverlay, discountTable, discountAmountTitle]);

    const calculatedPrice = useMemo(() => {
        if (listPrice && Number.isFinite(listPrice)) {
            if (discountTable.length > 0) {
                const applicableDiscount = discountTable.find(
                    (item) => quantity >= item.quantityFrom && quantity <= item.quantityTo
                );
                return applicableDiscount ? applicableDiscount.price : listPrice;
            }
            return listPrice;
        }
        return undefined;
    }, [listPrice, discountTable, quantity]);

    const priceElement = (
        <div className={S.currentPrice}>
            {Number.isFinite(calculatedPrice) ? (
                <>
                    {hasVariants && <FromComponent />}

                    {formatNumber(calculatedPrice, currentCulture, {
                        decimals: 2,
                    })}
                </>
            ) : (
                <DA_EmptyStateNoPrice />
            )}
        </div>
    );

    const renderDiscountPrice = () => (
        <div className={S.currentPrice}>
            <div className={S.prices}>
                <div>
                    <FromComponent />
                    {formatNumber(salesPrice, currentCulture, {
                        decimals: 2,
                    })}
                </div>
                <div className={S.discountedPrice}>
                    {formatNumber(listPrice, currentCulture, {
                        decimals: 2,
                    })}
                </div>
            </div>
        </div>
    );

    const priceElementWithInteraction =
        hasQuantityDiscounts || hasDiscount ? (
            isMobile ? (
                <div
                    role="button"
                    tabIndex={0}
                    onClick={openDynamicDiscountOverlay}
                    onKeyPress={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            openDynamicDiscountOverlay();
                        }
                    }}
                    className={S.priceClickable}
                >
                    {hasDiscount ? renderDiscountPrice() : priceElement}
                </div>
            ) : discountTable.length > 1 ? ( // Only show DA_Tooltip if there are multiple discounts
                <DA_Tooltip
                    text={
                        <div className={S.discountTable}>
                            {discountTable.map((item, index) => (
                                <div className={S.discountTableItem} key={index}>
                                    <span className={S.amount}>{`${
                                        item.quantityFrom
                                    } ${getDictionaryString("pcs")}`}</span>
                                    <span className={S.pricePerPcs}>
                                        {getDictionaryString("discount.pricePerPcs")}
                                    </span>
                                    <span className={S.price}>
                                        {formatNumber(item.price, currentCulture, {
                                            decimals: 2,
                                        })}
                                    </span>
                                </div>
                            ))}
                        </div>
                    }
                    icon={DA_IconNames.Info}
                    placement="top"
                >
                    {hasDiscount ? renderDiscountPrice() : priceElement}
                </DA_Tooltip>
            ) : hasDiscount ? (
                renderDiscountPrice()
            ) : (
                priceElement
            )
        ) : (
            priceElement
        );

    return (
        <div className={S.priceWrapper}>
            <div className={S.price}>{priceElementWithInteraction}</div>
        </div>
    );
};
